import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, Data } from '@angular/router';

import { Session } from '../services/session';

import { FLEET_CARD_APPLICATION_STATUS } from '@app/dashboard/fleet-cards/fleet-card-status';
import { Observable, pipe } from 'rxjs';

interface IFleetCardsGuard {
  status: FLEET_CARD_APPLICATION_STATUS | [FLEET_CARD_APPLICATION_STATUS];
  requiresAdmin?: boolean;
  redirectTo?: string;
  redirectUsingApplicationStatus?: boolean;
}

const REDIRECTION_URLS = {
  [FLEET_CARD_APPLICATION_STATUS.WAITLIST]: '/fleet-cards/waitlist',
  [FLEET_CARD_APPLICATION_STATUS.UPSELL]: '/fleet-cards/upsell',
  [FLEET_CARD_APPLICATION_STATUS.APPROVED]: '/fleet-cards/',
};

@Injectable()
export class FleetCardsGuard {
  constructor(
    private router: Router,
    private session: Session,
  ) { }

  public canActivate(route: ActivatedRouteSnapshot): boolean {
    const session: Session = this.session;
    const data: Data = route.data;
    const config: IFleetCardsGuard = data.fleetCardsGuard;
    const { status, requiresAdmin, redirectUsingApplicationStatus } = config;

    if (session.isDriver()) {
      this.router.navigate([config.redirectTo]);
      return false;
    }

    if (requiresAdmin && !session.isAdmin()) {
      if (session.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.APPROVED
        && session.hasFleetCardAdministrativePermissionEnabled()) {
        return true;
      }

      if (config.redirectTo) {
        this.router.navigate([config.redirectTo]);
      }
      return false;
    }

    if (Array.isArray(status)) {
      if (status.includes(session.getFleetCardApplicationStatus())) {
        return true;
      }
    } else {
      if (status === session.getFleetCardApplicationStatus()) {
        return true;
      }
    }

    if (config.redirectTo) {
      const redirectTo = redirectUsingApplicationStatus ? this.getRedirectionUrl(config.redirectTo) : config.redirectTo;
      this.router.navigate([redirectTo]);
    }

    return false;
  }

  public isAdminRoute(): boolean {
    return this.router.url.startsWith('/admin');
  }

  public appendAdminPrefix(url: string): string {
    return '/admin' + url;
  }

  public getRedirectionUrl(fallbackUrl: string) {
    let url = REDIRECTION_URLS[this.session.getFleetCardApplicationStatus()];
    if (url && this.session.isAdmin() && this.isAdminRoute()) {
      url = this.appendAdminPrefix(url);
    }
    return url || fallbackUrl;
  }
}
